import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import Button, { ButtonType } from 'inquiry/components/Button';
import { PhoneInput } from 'inquiry/components/FormControl';
import { sizeCss } from 'lib/inquiryStyles';
import ThemeSerialized, { TermsTextPosition } from 'lib/interfaces/ThemeSerialized';
import { ButtonComponentStyles, TextComponentStyles } from 'lib/interfaces/ui-components';
import { isRequired, withMessage } from 'lib/utils/formValidation';
import isValidPhone from 'lib/utils/formValidation/isValidPhone';
import FormGroup from 'verify/components/FormGroup';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import ThemedParagraph from 'verify/components/ThemedParagraph';

const DisclaimerContainer = styled.div<{ position: TermsTextPosition; theme: ThemeSerialized }>`
  display: block;
  margin-top: ${SPACINGS.base}px;
  ${({ position, theme }) =>
    position === TermsTextPosition.AboveButton &&
    css`
      padding-bottom: ${SPACINGS.base * 2}px;
      border-bottom: 1px solid ${theme.colorDivider};
      margin-bottom: ${SPACINGS.base * 2}px;
    `}
`;

const Disclaimer = styled(ThemedParagraph)<{ theme: ThemeSerialized }>`
  color: ${({ theme }) => theme.colorFontSmall};
  font-size: ${({ theme }) => theme.fontSizeSmall};
  line-height: ${({ theme }) => theme.lineHeightSmall};

  &,
  a {
    font-size: ${({ theme }) => theme.fontSizeSmall};
    line-height: ${({ theme }) => theme.lineHeightSmall};
  }
`;

const ThemedDisclaimer = styled(ThemedParagraph)<{
  styles: TextComponentStyles;
}>`
  ${({ styles }) => {
    /* TODO [strict-migration] The following line is not typed properly; please fix! */
    const untypedStyles = styles as any;
    return {
      fontSize: sizeCss(untypedStyles['font-size'].base.base) as any,
      lineHeight: sizeCss(untypedStyles['line-height'].base.base) as any,
      '& , a': {
        fontSize: sizeCss(untypedStyles['font-size'].base.base) as any,
        lineHeight: sizeCss(untypedStyles['line-height'].base.base) as any,
      },
    };
  }}
`;

export interface FormPhoneNumberProps {
  className?: string;
  labelContinue: string;
  defaultCountry: string;
  onCountryChange?: (countryCode: string) => void;
  disclaimerPosition?: TermsTextPosition;
  disclaimerStyle?: TextComponentStyles;
  disclaimerText?: string;
  buttonStyle?: ButtonComponentStyles;
  disabled?: boolean;
  // TODO: input SST styles
}

function FormPhoneNumberComponent(
  props: FormPhoneNumberProps & InjectedFormProps<{ 'phone-number': string }, FormPhoneNumberProps>,
) {
  const {
    defaultCountry,
    handleSubmit,
    labelContinue,
    pristine,
    submitting,
    onCountryChange = () => null,
    buttonStyle,
    disclaimerStyle,
    disclaimerText,
    disclaimerPosition = TermsTextPosition.BelowButton,
    disabled,
  } = props;

  const [t] = useTranslation();

  const validate = useMemo(() => {
    return {
      phoneNumber: [
        withMessage(t('database.form.validate-phone-number-valid'), isValidPhone),
        withMessage(t('database.form.validate-phone-number-required'), isRequired),
      ],
    };
  }, [t]);

  const disclaimer = (
    <>
      {disclaimerStyle != null ? (
        <ThemedDisclaimer styles={disclaimerStyle}>
          <Markdown>{disclaimerText ?? t('to-mobile.disclaimer')}</Markdown>
        </ThemedDisclaimer>
      ) : (
        <Disclaimer>
          <Markdown>{disclaimerText ?? t('to-mobile.disclaimer')}</Markdown>
        </Disclaimer>
      )}
    </>
  );

  return (
    <form onSubmit={handleSubmit}>
      <FormGroup>
        <label htmlFor='phone-number'>{t('phone-number.form.label')}</label>
        <Field
          autoComplete='phone'
          component={PhoneInput}
          disabled={disabled || submitting}
          id='phone-number'
          name='phone-number'
          type='tel'
          defaultCountry={defaultCountry}
          validate={validate.phoneNumber}
          onCountryChange={onCountryChange}
        />
      </FormGroup>

      {disclaimerPosition === TermsTextPosition.AboveButton && (
        <DisclaimerContainer position={TermsTextPosition.AboveButton}>
          {disclaimer}
        </DisclaimerContainer>
      )}

      <PositionableButtonContainer>
        <Button
          type='submit'
          buttonType={ButtonType.Primary}
          styles={buttonStyle}
          disabled={submitting}
          isPending={submitting}
          tabIndex={pristine || submitting ? -1 : 0}
        >
          {labelContinue}
        </Button>

        {disclaimerPosition === TermsTextPosition.BelowButton && (
          <DisclaimerContainer position={TermsTextPosition.BelowButton}>
            {disclaimer}
          </DisclaimerContainer>
        )}
      </PositionableButtonContainer>
    </form>
  );
}

const FormPhoneNumber = reduxForm<{ 'phone-number': string }, FormPhoneNumberProps>({
  form: 'phone-number',
})(FormPhoneNumberComponent);

export default FormPhoneNumber;
